import { useCallback, useEffect, useState } from 'react';

import { BluePrintsRead } from '@ariksa/compliance-policies/api';
import { each, filter, forEach, isEmpty, map } from 'lodash';
import { useSelector } from 'react-redux';

import { getBlueprintIcon } from 'containers/PolicyHub/utils';
import { selectSharedState } from 'containers/SharedState/selectors';

export const useBlueprintsMapping = () => {
  const { blueprints } = useSelector(selectSharedState);
  const [blueprintMapping, setBlueprintMapping] = useState<
    Record<string, BluePrintsRead>
  >({});
  const [envBlueprintMapping, setEnvBlueprintMapping] = useState<
    Record<string, BluePrintsRead[]>
  >({});
  const [alertingEnvBlueprintMapping, setAlertingEnvBlueprintMapping] = useState<
    Record<string, BluePrintsRead[]>
  >({});

  useEffect(() => {
    const options: Record<string, any> = {};
    forEach(blueprints.data, o => (options[o.id] = o));
    setBlueprintMapping(options);
  }, [blueprints.data]);

  //set environment id and blueprint mapping
  useEffect(() => {
    const options: Record<string, any> = {};
    forEach(blueprints.data, o => {
      each(o.environments, e => {
        if (!options[e]) options[e] = [o];
        else options[e].push(o);
      });
    });
    setEnvBlueprintMapping(options);
  }, [blueprints.data]);

  //set environment id and alerting blueprint mapping
  useEffect(() => {
    const options: Record<string, any> = {};
    forEach(blueprints.data, o => {
      each(o.alerting_environments, e => {
        if (!options[e]) options[e] = [o];
        else options[e].push(o);
      });
    });
    setAlertingEnvBlueprintMapping(options);
  }, [blueprints.data]);

  const getBlueprint = useCallback(
    (id: string) => {
      return blueprintMapping[id];
    },
    [blueprintMapping],
  );

  const getOptions = useCallback(
    (data: Record<string, any>[]) =>
      map(data, o => ({
        label: o?.name ?? o?.blueprint_name,
        value: o?.id ?? o?.blueprint_id,
        data: o,
        icon: getBlueprintIcon(
          o?.created_by !== 'system',
          o?.compliance ?? o?.name ?? o?.blueprint_name,
        ),
      })),
    [],
  );

  const getCategorizedBlueprintOptions = useCallback(
    (showOnlyWithAttachedEnv = false) => {
      let options: Record<string, any> = {
        CUSTOM: [],
        STANDARDS: [],
        ARIKSA: [],
      };

      each(blueprints?.data, o => {
        const option = {
          label: o?.name,
          value: o?.id,
          data: o,
          icon: getBlueprintIcon(
            o?.created_by !== 'system',
            o?.compliance ?? o?.name,
          ),
        };
        if (
          !showOnlyWithAttachedEnv ||
          (showOnlyWithAttachedEnv && !isEmpty(o.environments))
        ) {
          if (o?.created_by === 'system') {
            if (o?.name === 'Ariksa Best Practices')
              options.ARIKSA?.push(option);
            else options.STANDARDS?.push(option);
          } else options.CUSTOM?.push(option);
        }
      });

      let filteredOptions: Record<string, any>[] = [];
      each(options, (o, key) => {
        if (!isEmpty(o))
          filteredOptions.push({ label: key, options: o, value: key });
      });

      return filteredOptions;
    },
    [blueprints.data],
  );

  const getBlueprintOptions = useCallback(() => getOptions(blueprints.data), [
    blueprints?.data,
    getOptions,
  ]);

  const getComplianceBlueprintOptions = useCallback(
    () => getOptions(filter(blueprints.data, b => !!b?.compliance)),
    [blueprints?.data, getOptions],
  );

  const getBlueprintOptionsWithAll = useCallback(
    () => [
      { label: 'All Blueprints', value: undefined },
      ...getBlueprintOptions(),
    ],
    [getBlueprintOptions],
  );

  return {
    blueprintMapping,
    envBlueprintMapping,
    alertingEnvBlueprintMapping,
    getBlueprint,
    getBlueprintOptions,
    getBlueprintOptionsWithAll,
    getComplianceBlueprintOptions,
    getOptions,
    getCategorizedBlueprintOptions,
  };
};
